// Shared profile-identity fields so the sidebar (AppShell) and the Account
// page agree the instant either is saved, instead of the sidebar only
// picking up the change on its next remount/refresh — AppShell used to
// fetch its own copy via getProfile() independently of AccountPage's local
// state (2026-08-24 bug, caught by the user live: "Signed In As" updated on
// save, the left nav didn't until a page refresh). Mirrors the existing
// LanguageProvider pattern (src/lib/i18n/index.tsx) — one provider mounted
// once above the router in main.tsx, read by any screen that needs the
// current value.
// Originally businessProfile.tsx/useBusinessProfile (businessName only);
// broadened and renamed 2026-08-24 when the sidebar identity block also
// needed to reflect full_name live — same live-update requirement, same
// underlying profile row, so one shared context instead of two near-
// identical ones.
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

import { getAccount, getProfile } from "@/lib/profile";
import { supabase } from "@/lib/supabase";

type Ctx = {
  businessName: string;
  setBusinessName: (name: string) => void;
  fullName: string;
  setFullName: (name: string) => void;
};

const IdentityContext = createContext<Ctx | null>(null);

export function IdentityProvider({ children }: { children: ReactNode }) {
  const [businessName, setBusinessName] = useState("");
  const [fullName, setFullName] = useState("");

  // Mounted at the very top of the tree (main.tsx), above App's own
  // supabase.auth.getSession() — a single mount-time fetch could race
  // session hydration on page load, getProfile() would see no session yet,
  // return null, and this would stay stuck on the email fallback for the
  // rest of the tab's life (a real bug, caught live: "used to show Full
  // Name, now shows email"). Re-running on every auth state change, not
  // just once on mount, means a fetch that loses that race gets a second
  // real chance the moment the session actually resolves.
  useEffect(() => {
    let cancelled = false;
    function refresh() {
      Promise.all([getAccount().catch(() => null), getProfile().catch(() => null)]).then(([a, p]) => {
        if (cancelled) return;
        setBusinessName(a?.business_name ?? "");
        setFullName(p?.full_name ?? "");
      });
    }
    // AUTH-5: idempotent invite-acceptance check, run before refresh() so a
    // freshly-accepted invite's account is what gets fetched, not the stale
    // personal one from the instant before. A no-op (and safely swallowed,
    // including for a signed-out caller) when there's no matching invite —
    // see accept_pending_invite() in supabase/migrations/0027.
    function onAuthEvent() {
      Promise.resolve(supabase.rpc("accept_pending_invite"))
        .catch(() => {})
        .finally(refresh);
    }
    onAuthEvent();
    const { data: sub } = supabase.auth.onAuthStateChange(() => onAuthEvent());
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  return (
    <IdentityContext.Provider value={{ businessName, setBusinessName, fullName, setFullName }}>
      {children}
    </IdentityContext.Provider>
  );
}

export function useIdentity(): Ctx {
  const ctx = useContext(IdentityContext);
  if (!ctx) throw new Error("useIdentity must be used inside <IdentityProvider>");
  return ctx;
}
